import { useEffect, useMemo } from 'react'

import {
  clientLibraryDiscoveryPanelSetLoad,
  clientLibrarySuggestedLibrariesLoad,
} from '@disco/state-management/actions/client-library-discovery-panel-actions'
import {
  getDiscoveryPanelSet,
  getSuggestedLibraries,
} from '@disco/state-management/selectors/client-libraries'

import type { ClientLibraryDiscoveryPanelItem } from 'types/client-library'

import type {
  CatalogGroup,
  PickerCatalog,
} from 'components/search/search-columns/source-column/catalog-picker-modal/types'

import { useDispatch, useSelector } from 'hooks/use-redux'

type DiscoveryPanelSet = {
  id: string | number
  name: string
  client_libraries?: ClientLibraryDiscoveryPanelItem[]
}

export const FAVOURITES_GROUP_ID = 'g-favourites'

/**
 * Only approved catalogs are searchable, so curated sets are trimmed to those
 * and dropped when nothing is left.
 */
export const useSearchableCatalogs = (enabled: boolean) => {
  const dispatch = useDispatch()
  const suggested = useSelector(getSuggestedLibraries)
  const sets = useSelector(getDiscoveryPanelSet)

  useEffect(() => {
    if (!enabled) return
    if (!suggested.loaded && !suggested.loading && !suggested.error) {
      dispatch(clientLibrarySuggestedLibrariesLoad())
    }
    if (!sets.loaded && !sets.loading && !sets.error) {
      dispatch(clientLibraryDiscoveryPanelSetLoad())
    }
  }, [
    dispatch,
    enabled,
    suggested.loaded,
    suggested.loading,
    suggested.error,
    sets.loaded,
    sets.loading,
    sets.error,
  ])

  const catalogs = useMemo<PickerCatalog[]>(
    () =>
      (suggested.items as unknown as PickerCatalog[]).filter(
        c => c.status === 'approved',
      ),
    [suggested.items],
  )

  const groups = useMemo<CatalogGroup[]>(() => {
    const searchableIds = new Set(catalogs.map(c => c.id))
    const favouriteIds = catalogs.filter(c => c.is_favorite).map(c => c.id)
    const favourites: CatalogGroup[] =
      favouriteIds.length > 0
        ? [
            {
              id: FAVOURITES_GROUP_ID,
              name: 'Favourites',
              catalogIds: favouriteIds,
              isFavourites: true,
            },
          ]
        : []
    const curated = (sets.items as unknown as DiscoveryPanelSet[])
      .map(set => ({
        id: `set-${set.id}`,
        name: set.name,
        catalogIds: (set.client_libraries ?? [])
          .map(c => c.id)
          .filter(id => searchableIds.has(id)),
      }))
      .filter(group => group.catalogIds.length > 0)
    return [...favourites, ...curated]
  }, [catalogs, sets.items])

  return { catalogs, groups }
}
